/**
 * Recharge Bundle + Free Shipping Bar Integration
 * Updates the frozen shipping bar in real-time as customers select products in the bundle widget.
 * Bundle summary primary CTA: “Add to cart” in the button text becomes “Checkout” (handler still sends users to checkout).
 * Appends size info under bundle list h3 / quickview title (`?view=size-info-json`), with handle cache + prefetch
 * (pointerdown on rows, staggered batch after load). Multi-variant products return every variant's size metafields;
 * visibility syncs to the selected Recharge variant (`data-recharge-variant-id` / `?variant=`). Modal tabs: parallel
 * tab JSON prefetch on load, mouseover prefetch on rows, bundle observer rAF pass on modal mutations, sync paint
 * when HTML is cached (`?view=modal-tabs-json`), plus the same `runSizeInfoPass` schedule. Size-info uses a
 * `document.body` subtree observer plus every `.recharge-modal-wrapper` (Recharge portals quickview outside the
 * bundle widget); immediate pass when the mutation target sits under any modal wrapper, then the usual debounced pass.
 *
 * Subscription frequency modal (Recharge storefront, opened from bundle subscription picker):
 *   - Container: `[role="dialog"][aria-modal="true"]` inside `.recharge-storefront-theme`
 *   - Title: `span.recharge-heading.recharge-heading-h3` (e.g. "How often would you like to receive {bundle name}?")
 *     → theme rewrites to "Deliver every:" via `trimRechargeFrequencyLabels` / `frequencyNewTextWithoutSavePct`
 *   - Options: `ul[role="listbox"]` > `li[role="option"]` with `span.recharge-text-p3` labels ("One time only", "4 weeks", …)
 *   - Option `aria-label` mirrors visible text; leading "Every " and trailing ": save N%" are stripped; bare "week" → "1 week"
 *   - "One time only" is rewritten to PDP language ("One-time purchase") and moved to the bottom.
 *   - Re-render safe: `MutationObserver` on `document.body` (recharge-bundle template suffix only)
 *
 * Inside `.recharge-modal-wrapper`, before a line is in the bundle, a theme-only `recharge-bundle-modal-simple-add-btn`
 * ("Add", modal price row stays visible beside it) proxies the real list Add — not `recharge-bundle-row-add-btn`. Once the list card shows a
 * qty stepper, that control is removed and a `.recharge-bundle-modal-stepper-mirror` clone forwards +/- to the list row.
 * Match quickview `recharge-heading-h1` to `recharge-heading-h3`; aria-label fallback strips "Product N of M:". If the
 * modal column already has a live Recharge qty stepper (outside our mirror), theme extras are stripped.
 * Trustpilot Q&A: iframe posts { command: "new-question" } to the parent (see Trustpilot loader.js); we close the Recharge quickview when that fires from a widget inside bundle modal tabs. Same-document click kept as fallback.
 */
(function () {
  'use strict';

  var productIdToHandleCache = {};
  var productsFetchPromise = null;
  function ensureProductsCache() {
    if (!productsFetchPromise) {
      productsFetchPromise = fetch((window.Shopify?.routes?.root || '/') + 'products.json?limit=250')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = data && data.products;
          if (products) {
            products.forEach(function (p) {
              productIdToHandleCache[String(p.id)] = p.handle;
            });
          }
          return productIdToHandleCache;
        })
        .catch(function () { return {}; });
    }
    return productsFetchPromise;
  }
  ensureProductsCache();

  function getShippingBarData() {
    var el = document.querySelector('[data-shippingBar]');
    return {
      el: el,
      thresholdCents: el ? Math.max(0, parseInt(el.dataset.shippingThreshold, 10) || 15000) : 15000,
      cartFrozenTotal: el ? Math.max(0, parseInt(el.dataset.cartFrozenTotal, 10) || 0) : 0,
      eligibleMsg: el ? (el.dataset.shippingEligible || '') : '',
      progressMsgTemplate: el ? (el.dataset.shippingProgress || '') : ''
    };
  }

  var barData = getShippingBarData();
  var thresholdCents = barData.thresholdCents;
  var cartFrozenTotal = barData.cartFrozenTotal;
  var eligibleMsg = barData.eligibleMsg;
  var progressMsgTemplate = barData.progressMsgTemplate;

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return window.Shopify?.formatMoney ? window.Shopify.formatMoney(cents) : '$' + amount;
  }

  function updateShippingBarUI(combinedTotalCents) {
    var barEl = document.querySelector('[data-shippingBar]');
    if (!barEl) return;
    const progressBar = barEl.querySelector('.progress-bar');
    const deliveryShipping = barEl.querySelector('.delivery-shipping .paragraph-text');
    const tooltipInner = barEl.querySelector('.tooltip-inner');

    if (!progressBar || !deliveryShipping) return;

    const safeThreshold = thresholdCents > 0 ? thresholdCents : 15000;
    const progress = Math.min(100, Math.round((combinedTotalCents / safeThreshold) * 10000) / 100);
    const isEligible = combinedTotalCents >= safeThreshold;

    progressBar.style.width = progress + '%';

    if (tooltipInner) {
      tooltipInner.style.display = isEligible ? 'none' : '';
    }

    if (isEligible) {
      deliveryShipping.innerHTML = eligibleMsg;
    } else {
      const leftCents = Math.max(0, safeThreshold - combinedTotalCents);
      const leftFormatted = formatMoney(leftCents);
      const message = progressMsgTemplate.replace(/\$\{addMoreForFreeShip\}/gi, leftFormatted);
      deliveryShipping.innerHTML = message;
    }
  }

  function parseBundleTotalFromDOM() {
    const priceSpan = document.querySelector('.recharge-card .recharge-button-primary [aria-label*="Actual price"]');
    const addToCartBtn = document.querySelector('.recharge-card .recharge-button-primary');
    const container = priceSpan || addToCartBtn;
    if (!container) return 0;

    const text = container.textContent || '';
    const ariaLabel = container.getAttribute?.('aria-label') || '';
    let match = ariaLabel.match(/Actual price \$?([\d,]+\.?\d*)/i);
    if (match) {
      const value = parseFloat(match[1].replace(/,/g, ''));
      return Math.round(value * 100);
    }
    const priceMatches = text.match(/\$[\d,]+\.?\d*/g);
    if (priceMatches && priceMatches.length > 0) {
      const lastPrice = priceMatches[priceMatches.length - 1];
      const value = parseFloat(lastPrice.replace(/[$,]/g, ''));
      return Math.round(value * 100);
    }
    return 0;
  }

  var rechargeStickyTopRaf = null;
  var rechargeStickyTopSetupDone = false;

  function updateRechargeBundleStickyTopFromBar() {
    if (rechargeStickyTopRaf) cancelAnimationFrame(rechargeStickyTopRaf);
    rechargeStickyTopRaf = requestAnimationFrame(function () {
      rechargeStickyTopRaf = null;
      var wrap = document.querySelector('.recharge-shipping-bar-container');
      var fallback = 130;
      var h = wrap ? Math.ceil(wrap.getBoundingClientRect().height) : fallback;
      if (h < 1) h = fallback;
      document.documentElement.style.setProperty(
        '--recharge-bundle-sticky-top',
        h + 'px',
      );
    });
  }

  function setupRechargeBundleStickyTop() {
    updateRechargeBundleStickyTopFromBar();
    if (rechargeStickyTopSetupDone) return;
    rechargeStickyTopSetupDone = true;
    var resizeT;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(updateRechargeBundleStickyTopFromBar, 100);
    });
    var wrap = document.querySelector('.recharge-shipping-bar-container');
    if (wrap && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(function () {
        updateRechargeBundleStickyTopFromBar();
      }).observe(wrap);
    }
  }

  function refreshFromBundle() {
    const bundleTotalCents = parseBundleTotalFromDOM();
    const combinedTotal = cartFrozenTotal + bundleTotalCents;
    updateShippingBarUI(combinedTotal);
    updateRechargeBundleStickyTopFromBar();
    syncCheckoutDisclaimer();
    applyBundleSummaryPrimaryCheckoutLabel();
  }

  var RB_BUNDLE_DISCLAIMER_HOST = 'ffm-rb-bundle-disclaimer-host';
  var RB_DISCLAIMER_TOOLTIP_CLASS = 'ffm-rb-disclaimer-tooltip';

  function collectBundleDisclaimerRoots() {
    var roots = [];
    var seen = Object.create(null);
    function add(el) {
      if (!el || el.nodeType !== 1 || seen[el]) return;
      seen[el] = true;
      roots.push(el);
    }
    add(document.querySelector('#recharge-bundles'));
    add(document.querySelector('#recharge-storefront-bundling-widget'));
    add(document.querySelector('.recharge-bundle-collections'));
    add(document.querySelector('.recharge-card'));
    var bs;
    var bsi;
    var bsNodes = document.querySelectorAll('[data-testid^="bundle-summary"]');
    for (bsi = 0; bsi < bsNodes.length; bsi++) {
      add(bsNodes[bsi]);
    }
    var shopSections = document.querySelectorAll(
      'shopify-section[class*="bundles"], #MainContent, [id*="shopify-section"]',
    );
    var si;
    for (si = 0; si < shopSections.length; si++) {
      var inner = shopSections[si].querySelector && shopSections[si].querySelector('#recharge-bundles');
      if (inner) add(shopSections[si]);
    }
    return roots;
  }

  function normalizeButtonLabelText(raw) {
    return String(raw || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function labelLooksLikeAddToCart(t) {
    if (!t) return false;
    if (/\badd\s*to\s*cart\b/i.test(t)) return true;
    if (/\badd[\s\u00a0]+to[\s\u00a0]+cart\b/i.test(t)) return true;
    return t.indexOf('add') !== -1 && t.indexOf('cart') !== -1 && t.length < 48;
  }

  /**
   * Bundle-summary primary CTA: “add to cart” or prior “Checkout” / combined label. Disclaimer must match after relabel.
   * Recharge may render the CTA with inline price — match normalized prefix, not exact `checkout` only.
   */
  function labelLooksLikeBundleSummaryDisclaimerAnchor(t) {
    if (labelLooksLikeAddToCart(t)) return true;
    if (!t) return false;
    var n = normalizeButtonLabelText(t);
    if (n === 'checkout') return true;
    if (/^checkout\b/.test(n)) return true;
    if (n.indexOf('add to cart') !== -1 && n.indexOf('checkout') !== -1) return true;
    return false;
  }

  var RB_BUNDLE_PRIMARY_CHECKOUT_LABEL = 'Checkout';
  var RB_ADD_TO_CART_PHRASE_IN_TEXT_RE = /add[\s\u00a0]+to[\s\u00a0]+cart/gi;

  /**
   * Only mutates Text node values so Recharge’s nested divs/classes stay intact.
   * Replaces every “add to cart” phrase in each text node (never `button.textContent`).
   */
  function replacePrimaryButtonAddToCartTextOnly(btn, replacement) {
    if (!btn || typeof document.createTreeWalker !== 'function') return false;
    var walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT, null);
    var textNode;
    var changed = false;
    while ((textNode = walker.nextNode())) {
      var raw = textNode.nodeValue;
      if (!raw) continue;
      RB_ADD_TO_CART_PHRASE_IN_TEXT_RE.lastIndex = 0;
      var next = raw.replace(RB_ADD_TO_CART_PHRASE_IN_TEXT_RE, replacement);
      if (next !== raw) {
        textNode.nodeValue = next;
        changed = true;
      }
    }
    return changed;
  }

  /**
   * Recharge sometimes splits label into three sibling elements: Add / to / cart.
   * Collapse that sequence to one "Checkout" token while preserving wrapper classes.
   */
  function replaceSplitAddToCartTokenElements(btn, replacement) {
    if (!btn || !btn.querySelectorAll) return false;
    var tokenNodes = [];
    btn.querySelectorAll('*').forEach(function (el) {
      if (!el || !el.childNodes || el.childNodes.length !== 1) return;
      var child = el.firstChild;
      if (!child || child.nodeType !== 3) return;
      var token = (child.nodeValue || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/[:;,.!?]+$/g, '');
      if (!token) return;
      if (token === 'add' || token === 'to' || token === 'cart') {
        tokenNodes.push({ el: el, token: token });
      }
    });

    var i;
    for (i = 0; i <= tokenNodes.length - 3; i++) {
      var a = tokenNodes[i];
      var b = tokenNodes[i + 1];
      var c = tokenNodes[i + 2];
      if (a.token !== 'add' || b.token !== 'to' || c.token !== 'cart') continue;
      if (
        !a.el.parentElement ||
        a.el.parentElement !== b.el.parentElement ||
        b.el.parentElement !== c.el.parentElement
      ) {
        continue;
      }
      a.el.textContent = replacement;
      b.el.remove();
      c.el.remove();
      return true;
    }
    return false;
  }

  /**
   * Bundle summary primary CTAs: every “add to cart” phrase in text nodes becomes "Checkout" (handler unchanged).
   * Also handles split label tokens rendered as Add / to / cart in separate elements.
   * Scoped to bundle widget roots, excludes quickview modal and theme proxy buttons.
   */
  function applyBundleSummaryPrimaryCheckoutLabel() {
    var roots = collectBundleDisclaimerRoots();
    if (!roots.length) return;
    var ri;
    var root;
    var buttons;
    var i;
    var btn;
    var t;
    for (ri = 0; ri < roots.length; ri++) {
      root = roots[ri];
      if (!root || !root.querySelectorAll) continue;
      buttons = root.querySelectorAll('button');
      for (i = 0; i < buttons.length; i++) {
        btn = buttons[i];
        if (!isRechargePrimaryCtaButton(btn)) continue;
        if (
          btn.closest &&
          (btn.closest('.recharge-modal-wrapper') ||
            btn.closest('[class*="recharge-modal-wrapper"]'))
        ) {
          continue;
        }
        if (btn.classList.contains('recharge-bundle-row-add-btn')) continue;
        if (btn.classList.contains('recharge-bundle-modal-simple-add-btn')) continue;
        t = btn.textContent || '';
        if (/^checkout$/i.test(normalizeButtonLabelText(t))) continue;
        var changed = replacePrimaryButtonAddToCartTextOnly(btn, RB_BUNDLE_PRIMARY_CHECKOUT_LABEL);
        if (!changed) changed = replaceSplitAddToCartTokenElements(btn, RB_BUNDLE_PRIMARY_CHECKOUT_LABEL);
        if (!changed) continue;
      }
    }
  }

  function isRechargePrimaryCtaButton(btn) {
    if (!btn || btn.tagName !== 'BUTTON') return false;
    if (btn.classList.contains('recharge-button-primary')) return true;
    var c = typeof btn.className === 'string' ? btn.className : '';
    return c.indexOf('recharge-button-primary') !== -1;
  }

  function findAllBundleSummaryAddToCartButtons() {
    var roots = collectBundleDisclaimerRoots();
    if (!roots.length) return [];

    function gather(strictExcludeProductRow) {
      var candidates = [];
      var ri;
      var root;
      var buttons;
      var i;
      var btn;
      var t;
      for (ri = 0; ri < roots.length; ri++) {
        root = roots[ri];
        if (!root || !root.querySelectorAll) continue;
        buttons = root.querySelectorAll('button');
        for (i = 0; i < buttons.length; i++) {
          btn = buttons[i];
          if (!isRechargePrimaryCtaButton(btn)) continue;
          if (
            btn.closest &&
            (btn.closest('.recharge-modal-wrapper') ||
              btn.closest('[class*="recharge-modal-wrapper"]'))
          ) {
            continue;
          }
          if (btn.classList.contains('recharge-bundle-row-add-btn')) continue;
          if (strictExcludeProductRow && btn.closest && btn.closest('.recharge-bundle-product-item')) {
            continue;
          }
          t = normalizeButtonLabelText(btn.textContent);
          if (!labelLooksLikeBundleSummaryDisclaimerAnchor(t)) continue;
          candidates.push(btn);
        }
      }
      return candidates;
    }

    var strict = gather(true);
    var loose = gather(false);
    var merged = [];
    var seenWeak = typeof WeakSet === 'function' ? new WeakSet() : null;
    var seenArr = seenWeak ? null : [];
    var bi;
    var b;
    function pushUnique(btn) {
      if (!btn) return;
      if (seenWeak) {
        if (seenWeak.has(btn)) return;
        seenWeak.add(btn);
      } else if (seenArr.indexOf(btn) !== -1) {
        return;
      } else {
        seenArr.push(btn);
      }
      merged.push(btn);
    }
    for (bi = 0; bi < strict.length; bi++) {
      pushUnique(strict[bi]);
    }
    for (bi = 0; bi < loose.length; bi++) {
      pushUnique(loose[bi]);
    }

    merged.sort(function (x, y) {
      var pos = x.compareDocumentPosition(y);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    // Summary CTAs can live only under [data-testid^="bundle-summary"] (outside other roots); pick those up too.
    var summaryShells = document.querySelectorAll('[data-testid^="bundle-summary"]');
    var si;
    var shell;
    var shellBtns;
    var sj;
    var sb;
    var st;
    for (si = 0; si < summaryShells.length; si++) {
      shell = summaryShells[si];
      if (!shell || !shell.querySelectorAll) continue;
      shellBtns = shell.querySelectorAll('button');
      for (sj = 0; sj < shellBtns.length; sj++) {
        sb = shellBtns[sj];
        if (!isRechargePrimaryCtaButton(sb)) continue;
        if (
          sb.closest &&
          (sb.closest('.recharge-modal-wrapper') ||
            sb.closest('[class*="recharge-modal-wrapper"]'))
        ) {
          continue;
        }
        if (sb.classList.contains('recharge-bundle-row-add-btn')) continue;
        st = normalizeButtonLabelText(sb.textContent);
        if (!labelLooksLikeBundleSummaryDisclaimerAnchor(st)) continue;
        pushUnique(sb);
      }
    }

    merged.sort(function (x, y) {
      var pos2 = x.compareDocumentPosition(y);
      if (pos2 & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos2 & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    return merged;
  }

  function scheduleDisclaimerPlacementRetries() {
    if (!document.getElementById('rb-checkout-disclaimer-config')) return;
    if (window.__ffmRbDisclaimerRetryScheduled) return;
    window.__ffmRbDisclaimerRetryScheduled = true;
    var delays = [0, 250, 800, 2000, 4000];
    var di;
    for (di = 0; di < delays.length; di++) {
      (function (ms) {
        setTimeout(function () {
          syncCheckoutDisclaimer();
          applyBundleSummaryPrimaryCheckoutLabel();
        }, ms);
      })(delays[di]);
    }
  }

  function removeAllDisclaimerHosts() {
    document.querySelectorAll('.' + RB_BUNDLE_DISCLAIMER_HOST).forEach(function (n) {
      n.remove();
    });
  }

  function findDisclaimerRowWrap(btn) {
    if (!btn) return null;
    var inner = btn.closest('._17o99wpjc._17o99wplr');
    if (inner && inner.contains(btn)) return inner;
    return btn.closest('._15b7gxl0._17o99wp15l');
  }

  function insertDisclaimerHostForButton(btn, host) {
    var rowWrap = findDisclaimerRowWrap(btn);
    var slotParent = btn.closest('._15b7gxl0._18gma4r0') || btn.closest('._18gma4r0');
    if (rowWrap && slotParent && slotParent.contains(rowWrap)) {
      rowWrap.insertAdjacentElement('afterend', host);
      return;
    }
    if (slotParent && slotParent.contains(btn)) {
      slotParent.appendChild(host);
      return;
    }
    if (rowWrap && rowWrap.parentNode) {
      rowWrap.insertAdjacentElement('afterend', host);
      return;
    }
    if (btn && btn.parentNode) {
      btn.insertAdjacentElement('afterend', host);
    }
  }

  function findExistingDisclaimerHostForButton(btn, index, buttonsLength) {
    var want = String(index);
    var strict = (buttonsLength || 0) > 1;
    function indexMatches(attr) {
      if (attr === want) return true;
      if (!strict && attr == null) return true;
      return false;
    }
    var rowWrap = findDisclaimerRowWrap(btn);
    if (rowWrap) {
      var n = rowWrap.nextElementSibling;
      var g = 0;
      while (n && g < 48) {
        if (
          n.classList &&
          n.classList.contains(RB_BUNDLE_DISCLAIMER_HOST) &&
          indexMatches(n.getAttribute('data-ffm-disclaimer-index'))
        ) {
          return n;
        }
        n = n.nextElementSibling;
        g++;
      }
    }
    var n2 = btn.nextElementSibling;
    if (
      n2 &&
      n2.classList &&
      n2.classList.contains(RB_BUNDLE_DISCLAIMER_HOST) &&
      indexMatches(n2.getAttribute('data-ffm-disclaimer-index'))
    ) {
      return n2;
    }
    return null;
  }

  /**
   * Host is inserted after `findDisclaimerRowWrap(btn)` (legacy). Walk forward from that wrap; also allow
   * `btn.nextElementSibling` when the host was placed after the button (fallback insert).
   * `expectedIndex` must match `host`’s `data-ffm-disclaimer-index` so two CTAs sharing one rowWrap do not
   * both “claim” the first disclaimer in the sibling chain (that was removing the 2nd host immediately).
   */
  function disclaimerHostStillAnchoredToItsButton(host, btn, expectedIndex) {
    if (!host || !btn || !host.isConnected || !btn.isConnected) return false;
    var hostIdx = parseInt(host.getAttribute('data-ffm-disclaimer-index'), 10);
    if (
      expectedIndex != null &&
      !isNaN(expectedIndex) &&
      !isNaN(hostIdx) &&
      hostIdx !== expectedIndex
    ) {
      return false;
    }
    if (btn.nextElementSibling === host) return true;
    var rowWrap = findDisclaimerRowWrap(btn);
    if (rowWrap) {
      var parent = rowWrap.parentElement;
      if (parent && parent.contains(host)) {
        var n = rowWrap.nextElementSibling;
        var guard = 0;
        while (n && guard < 60) {
          if (n === host) return true;
          if (n.contains && n.contains(host)) return true;
          n = n.nextElementSibling;
          guard++;
        }
      }
    }
    return false;
  }

  function disclaimerHostOrphaned(host, buttons) {
    if (!host.isConnected) return true;
    var idx = parseInt(host.getAttribute('data-ffm-disclaimer-index'), 10);
    if (isNaN(idx)) return true;
    if (buttons[idx] && buttons[idx].isConnected) {
      return !disclaimerHostStillAnchoredToItsButton(host, buttons[idx], idx);
    }
    // Recharge sometimes repaints so `findAllBundleSummary…` briefly returns fewer CTAs than hosts;
    // don’t remove a higher-index host until the button list catches up (avoids micro-flash removal).
    var hostCount = document.querySelectorAll('.' + RB_BUNDLE_DISCLAIMER_HOST).length;
    if (buttons.length && hostCount > buttons.length && idx >= buttons.length) return false;
    var bi;
    for (bi = 0; bi < buttons.length; bi++) {
      if (!buttons[bi] || !buttons[bi].isConnected) continue;
      if (disclaimerHostStillAnchoredToItsButton(host, buttons[bi], bi)) return false;
    }
    return true;
  }

  function createDisclaimerHostBlock(text, index) {
    var tipId = 'ffm-rb-disclaimer-tip-' + index;
    var host = document.createElement('div');
    host.className =
      RB_BUNDLE_DISCLAIMER_HOST + ' w-100 d-flex flex-column align-items-start mt-4 px-2';
    host.setAttribute('data-ffm-disclaimer-index', String(index));
    var tip = document.createElement('div');
    tip.id = tipId;
    tip.className = RB_DISCLAIMER_TOOLTIP_CLASS + ' small text-start';
    tip.setAttribute('role', 'region');
    tip.setAttribute('aria-label', 'Checkout disclaimer');
    tip.innerHTML = text;
    host.appendChild(tip);
    return host;
  }

  function syncCheckoutDisclaimer() {
    var cfgEl = document.getElementById('rb-checkout-disclaimer-config');
    var cfg;
    if (cfgEl) {
      try {
        cfg = JSON.parse(cfgEl.textContent || '{}');
      } catch (err) {
        cfg = null;
      }
    } else {
      cfg = null;
    }
    if (!cfg || !cfg.enabled) {
      removeAllDisclaimerHosts();
      return;
    }
    var text = cfg.text != null ? String(cfg.text).trim() : '';
    if (!text) {
      removeAllDisclaimerHosts();
      return;
    }
    var buttons = findAllBundleSummaryAddToCartButtons();
    if (!buttons.length) {
      removeAllDisclaimerHosts();
      return;
    }

    var bi;
    document.querySelectorAll('.' + RB_BUNDLE_DISCLAIMER_HOST).forEach(function (host) {
      if (disclaimerHostOrphaned(host, buttons)) host.remove();
    });

    for (bi = 0; bi < buttons.length; bi++) {
      var btn = buttons[bi];
      if (!btn.parentNode) continue;
      var next = findExistingDisclaimerHostForButton(btn, bi, buttons.length);
      if (next) {
        next.setAttribute('data-ffm-disclaimer-index', String(bi));
        var legacyTrigger = next.querySelector('.ffm-rb-disclaimer-trigger');
        if (legacyTrigger) {
          legacyTrigger.remove();
        }
        var existingTip = next.querySelector('.' + RB_DISCLAIMER_TOOLTIP_CLASS);
        if (!existingTip) {
          var tipNew = document.createElement('div');
          tipNew.id = 'ffm-rb-disclaimer-tip-' + bi;
          tipNew.className = RB_DISCLAIMER_TOOLTIP_CLASS + ' small text-start';
          tipNew.setAttribute('role', 'region');
          tipNew.setAttribute('aria-label', 'Checkout disclaimer');
          tipNew.innerHTML = text;
          next.appendChild(tipNew);
        } else {
          existingTip.innerHTML = text;
          var tid = existingTip.id || 'ffm-rb-disclaimer-tip-' + bi;
          if (existingTip.id !== tid) {
            existingTip.id = tid;
          }
          existingTip.setAttribute('role', 'region');
          if (!existingTip.getAttribute('aria-label')) {
            existingTip.setAttribute('aria-label', 'Checkout disclaimer');
          }
        }
        continue;
      }
      var host = createDisclaimerHostBlock(text, bi);
      insertDisclaimerHostForButton(btn, host);
    }
  }

  function getBundleObserveRoot() {
    var card = document.querySelector('.recharge-card');
    return (
      document.querySelector('.recharge-bundle-collections') ||
      (card && card.closest('[id*="recharge"]')) ||
      document.querySelector('#recharge-bundles') ||
      document.querySelector('#recharge-storefront-bundling-widget') ||
      document.querySelector('.recharge-modal-wrapper') ||
      document.querySelector('[class*="recharge-modal-wrapper"]') ||
      card
    );
  }

  function getRechargeModalRoot() {
    return (
      document.querySelector('.recharge-modal-wrapper') ||
      document.querySelector('[class*="recharge-modal-wrapper"]')
    );
  }

  /**
   * Recharge may leave multiple modal wrappers in the DOM; the first match is often a hidden shell.
   * Tabs + fetch must target the visible instance so the host mounts where the customer sees it.
   */
  function getVisibleRechargeModalRoot() {
    var list = document.querySelectorAll(
      '.recharge-modal-wrapper, [class*="recharge-modal-wrapper"]',
    );
    var i;
    var el;
    var lastVis = null;
    for (i = 0; i < list.length; i++) {
      el = list[i];
      if (!el || el.nodeType !== 1) continue;
      if (el.hasAttribute('hidden')) continue;
      var st = window.getComputedStyle(el);
      if (st.display === 'none' || st.visibility === 'hidden') continue;
      var r = el.getBoundingClientRect();
      if (r.width < 2 && r.height < 2) continue;
      lastVis = el;
    }
    return lastVis;
  }

  function getRechargeModalRootForTabs() {
    var vis = getVisibleRechargeModalRoot();
    if (vis) return vis;
    var all = document.querySelectorAll(
      '.recharge-modal-wrapper, [class*="recharge-modal-wrapper"]',
    );
    var i;
    var el;
    for (i = 0; i < all.length; i++) {
      el = all[i];
      if (el && el.querySelector && el.querySelector('.recharge-heading-h1')) {
        return el;
      }
    }
    return getRechargeModalRoot();
  }

  /**
   * Bundle list and modal are often in different DOM subtrees (modal portaled under body). Union both so we
   * still find `.recharge-heading-h3` in the list when a hidden modal node exists early on the page.
   * Recharge may keep multiple `.recharge-modal-wrapper` nodes; `querySelector` only returns the first,
   * which is often a hidden shell — include every modal wrapper + the visible instance so quickview titles
   * are still discovered.
   */
  function getSizeInfoSearchRoots() {
    var o = getBundleObserveRoot();
    var roots = [];
    var seen = new Set();

    function pushRoot(r) {
      if (!r || r.nodeType !== 1 || seen.has(r)) return;
      seen.add(r);
      roots.push(r);
    }

    pushRoot(o);

    var vis = getVisibleRechargeModalRoot();
    pushRoot(vis);

    var modals = document.querySelectorAll(
      '.recharge-modal-wrapper, [class*="recharge-modal-wrapper"]',
    );
    var mi;
    var el;
    for (mi = 0; mi < modals.length; mi++) {
      el = modals[mi];
      if (!el) continue;
      if (o && o.contains(el) && el !== o) continue;
      pushRoot(el);
    }

    return roots;
  }

  var actualPriceAriaRe = /Actual price \$?([\d,]+\.?\d*)/i;

  function parseActualPriceLabel(span) {
    var label = span.getAttribute('aria-label') || '';
    var m = label.match(actualPriceAriaRe);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ''));
    if (isNaN(n)) return null;
    return '$' + n.toFixed(2);
  }

  function isThemeInjectRowAddBtn(el) {
    if (!el || !el.classList) return false;
    return (
      el.classList.contains('recharge-bundle-row-add-btn') ||
      el.classList.contains('recharge-bundle-modal-simple-add-btn')
    );
  }

  /** First real Recharge primary in scope; skips theme-injected proxy Add. */
  function firstNativeRechargePrimaryIn(scope) {
    if (!scope || !scope.querySelectorAll) return null;
    var nodes = scope.querySelectorAll('button.recharge-button-primary');
    var i;
    var n;
    for (i = 0; i < nodes.length; i++) {
      n = nodes[i];
      if (isThemeInjectRowAddBtn(n)) continue;
      return n;
    }
    nodes = scope.querySelectorAll('button[class*="recharge-button-primary"]');
    for (i = 0; i < nodes.length; i++) {
      n = nodes[i];
      if (isThemeInjectRowAddBtn(n)) continue;
      return n;
    }
    return null;
  }

  function findRowAddTrigger(priceSpan) {
    var cell =
      priceSpan.closest('[role="gridcell"]') ||
      priceSpan.closest('.recharge-bundle-product-item') ||
      priceSpan.closest('[data-recharge-product-id]');
    if (cell) {
      var b = firstNativeRechargePrimaryIn(cell);
      if (b) return b;
    }
    var modal =
      priceSpan.closest('.recharge-modal-wrapper') ||
      priceSpan.closest('[class*="recharge-modal-wrapper"]');
    var p = priceSpan.parentElement;
    for (var d = 0; d < 22 && p && p !== modal; d++) {
      if (p.getAttribute && p.getAttribute('role') === 'gridcell') {
        b = firstNativeRechargePrimaryIn(p);
        if (b) return b;
      }
      p = p.parentElement;
    }
    return null;
  }

  function resolvePriceColumn(span) {
    var byHash = span.closest('div._18gma4r0');
    if (byHash) return byHash;
    var nativeHost = span.closest('div._17o99wplo') || span.parentElement;
    if (nativeHost && nativeHost.parentElement) return nativeHost.parentElement;
    return nativeHost || span.parentElement;
  }

  function normalizeProductTitleForMatch(s) {
    return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /** "Product 1 of 20: Title" → "Title" (bundle list buttons / labels). */
  function stripLeadingProductIndexPrefix(s) {
    return (s || '')
      .replace(/^\s*product\s+\d+\s+of\s+\d+\s*:\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  var BUNDLE_PRODUCTS_CONTAINER_SEL =
    '.recharge-bundle-products-container, [class*="recharge-bundle-products-container"]';

  function queryAllRechargeBundleProductsContainers() {
    var list = document.querySelectorAll(BUNDLE_PRODUCTS_CONTAINER_SEL);
    var arr = [];
    var i;
    var el;
    for (i = 0; i < list.length; i++) {
      el = list[i];
      if (arr.indexOf(el) === -1) arr.push(el);
    }
    if (arr.length) return arr;
    var fb =
      document.querySelector('.recharge-bundle-collections') ||
      document.querySelector('#recharge-storefront-bundling-widget') ||
      document.querySelector('[class*="bundle-products-container"]');
    return fb ? [fb] : [];
  }

  /**
   * Modal is often portaled under body; walk up and find the first products container in an ancestor
   * subtree (not inside the modal). Used to try the “current” collection before scanning the rest.
   */
  function findNearestBundleProductsContainer(modalRoot) {
    if (!modalRoot) return null;
    var p = modalRoot.parentElement;
    var d;
    var list;
    var i;
    var el;
    for (d = 0; d < 35 && p; d++, p = p.parentElement) {
      if (p.nodeType !== 1) continue;
      list = p.querySelectorAll(BUNDLE_PRODUCTS_CONTAINER_SEL);
      for (i = 0; i < list.length; i++) {
        el = list[i];
        if (modalRoot.contains(el)) continue;
        return el;
      }
    }
    return null;
  }

  /** Every bundle list on the page; nearest-to-modal first, then the rest in document order (deduped). */
  function getBundleProductsContainersInPriorityOrder(modalRoot) {
    var all = queryAllRechargeBundleProductsContainers();
    var near = findNearestBundleProductsContainer(modalRoot);
    if (!near || all.indexOf(near) === -1) return all;
    return [near].concat(
      all.filter(function (c) {
        return c !== near;
      }),
    );
  }

  function findBundleAddButtonAcrossBundleContainers(modalRoot, rawTitle) {
    if (!rawTitle) return null;
    var containers = getBundleProductsContainersInPriorityOrder(modalRoot);
    var i;
    var btn;
    for (i = 0; i < containers.length; i++) {
      btn = findBundleAddButtonForQuickviewTitle(containers[i], rawTitle);
      if (btn) return btn;
    }
    return null;
  }

  /**
   * Product title in quickview: h1 often lives in a sibling column (same parent as price), not under gridcell.
   */
  function getQuickviewHeadingTitle(modalRoot, priceSpan) {
    var priceHost = priceSpan.closest('div._17o99wplo');
    var anc;
    var d;
    var h1;
    for (
      anc = priceHost && priceHost.parentElement, d = 0;
      anc && d < 12 && modalRoot.contains(anc);
      anc = anc.parentElement, d++
    ) {
      h1 = anc.querySelector('.recharge-heading-h1');
      if (h1 && (!priceHost || !priceHost.contains(h1))) {
        return (h1.textContent || '').replace(/\s+/g, ' ').trim();
      }
    }
    var row =
      priceSpan.closest('.recharge-bundle-product-item') ||
      priceSpan.closest('[role="gridcell"]') ||
      priceSpan.closest('[role="row"]');
    h1 = row && row.querySelector('.recharge-heading-h1');
    if (!h1) h1 = modalRoot.querySelector('.recharge-heading-h1');
    if (!h1) return '';
    return (h1.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Finds the bundle list Add button whose aria-label best matches the quickview title.
   */
  function findBundleAddButtonByAriaTitle(container, rawTitle) {
    var t = normalizeProductTitleForMatch(rawTitle);
    if (!t || !container || !container.querySelectorAll) return null;
    var modalWrap = getRechargeModalRoot();
    var candidates = container.querySelectorAll('button.recharge-button-primary');
    var i;
    var btn;
    var al;
    var bestBtn = null;
    var bestScore = 0;
    var score;
    for (i = 0; i < candidates.length; i++) {
      btn = candidates[i];
      if (isThemeInjectRowAddBtn(btn)) continue;
      if (modalWrap && modalWrap.contains(btn)) continue;
      al = normalizeProductTitleForMatch(
        stripLeadingProductIndexPrefix(btn.getAttribute('aria-label') || ''),
      );
      if (!al) continue;
      if (al === t) score = 1000 + al.length;
      else if (al.indexOf(t) !== -1) score = 500 + t.length;
      else if (t.indexOf(al) !== -1) score = 400 + al.length;
      else continue;
      if (score > bestScore) {
        bestScore = score;
        bestBtn = btn;
      }
    }
    return bestBtn;
  }

  /**
   * Best product card in one container by matching `recharge-heading-h3` to title. Returns { card, score }.
   */
  function findBestBundleCardByH3TitleInContainer(container, rawTitle) {
    var t = normalizeProductTitleForMatch(rawTitle);
    if (!t || !container || !container.querySelectorAll) return null;
    var modalWrap = getRechargeModalRoot();
    var headings = container.querySelectorAll(
      '.recharge-heading-h3, .recharge-heading.recharge-heading-h3, [role="heading"][aria-level="3"].recharge-heading',
    );
    var i;
    var h3;
    var h3Norm;
    var score;
    var bestScore = 0;
    var bestCard = null;
    var card;
    for (i = 0; i < headings.length; i++) {
      h3 = headings[i];
      if (modalWrap && modalWrap.contains(h3)) continue;
      h3Norm = normalizeProductTitleForMatch(
        stripLeadingProductIndexPrefix(h3.textContent || ''),
      );
      if (!h3Norm) continue;
      if (h3Norm === t) score = 1000 + h3Norm.length;
      else if (h3Norm.indexOf(t) !== -1) score = 500 + t.length;
      else if (t.indexOf(h3Norm) !== -1) score = 400 + h3Norm.length;
      else continue;
      if (score > bestScore) {
        bestScore = score;
        card =
          h3.closest('.recharge-bundle-product-item') ||
          h3.closest('.recharge-card') ||
          h3.closest('[data-recharge-product-id]') ||
          h3.parentElement;
        bestCard = card;
      }
    }
    if (!bestCard || bestScore < 400) return null;
    return { card: bestCard, score: bestScore };
  }

  function findBestBundleCardForQuickviewTitle(modalRoot, rawTitle) {
    var containers = getBundleProductsContainersInPriorityOrder(modalRoot);
    var bestCard = null;
    var bestScore = 0;
    var ci;
    var m;
    for (ci = 0; ci < containers.length; ci++) {
      m = findBestBundleCardByH3TitleInContainer(containers[ci], rawTitle);
      if (m && m.score > bestScore) {
        bestScore = m.score;
        bestCard = m.card;
      }
    }
    return bestCard;
  }

  /** Wrapper that contains decrement, number input, and increment on a bundle list card (live Recharge node). */
  function getQuantityStepperRowFromCard(card) {
    if (!card || !card.querySelector) return null;
    var inc = card.querySelector('[data-testid="increment"]');
    var dec = card.querySelector('[data-testid="decrement"]');
    var inp = card.querySelector('input[type="number"]');
    if (!inc || !dec || !inp) return null;
    var pInc = inc.parentElement;
    var pDec = dec.parentElement;
    var pInp = inp.parentElement;
    if (pInc && pInc === pDec && pInc === pInp) return pInc;
    var wrap = inc.closest('div._17o99wpjc');
    if (wrap && wrap.contains(dec) && wrap.contains(inp)) return wrap;
    return null;
  }

  /**
   * Recharge-owned qty row already in the quickview column (not our clone inside
   * .recharge-bundle-modal-stepper-mirror). Prefer this so we do not stack a mirror
   * on top of a live stepper or forward clicks to stale list nodes after re-renders.
   */
  function getLiveQuantityStepperRowInModalColumn(col) {
    if (!col || !col.querySelectorAll) return null;
    var incs = col.querySelectorAll('[data-testid="increment"]');
    var i;
    var inc;
    var wrap;
    var dec;
    var inp;
    for (i = 0; i < incs.length; i++) {
      inc = incs[i];
      if (!inc) continue;
      if (inc.closest && inc.closest('.recharge-bundle-modal-stepper-mirror')) continue;
      wrap = inc.closest('div._17o99wpjc');
      if (!wrap || !wrap.querySelector) continue;
      dec = wrap.querySelector('[data-testid="decrement"]');
      inp = wrap.querySelector('input[type="number"]');
      if (dec && inp) return wrap;
    }
    return null;
  }

  /** After Recharge updates the list stepper, copy qty into the modal clone (avoid rebuilding the mirror each click). */
  function queueMirrorQtySyncFromSource(mirrorRow, getSourceStepperRow) {
    function pull() {
      var sr = getSourceStepperRow();
      var sInp = sr && sr.querySelector('input[type="number"]');
      var mInp = mirrorRow.querySelector('input[type="number"]');
      if (mInp && sInp) mInp.value = sInp.value;
    }
    window.setTimeout(pull, 0);
    window.setTimeout(pull, 72);
  }

  function wireModalStepperMirror(mirrorRow, getSourceStepperRow) {
    var mInp = mirrorRow.querySelector('input[type="number"]');
    function bindQtyButton(mEl) {
      if (!mEl || !mEl.getAttribute) return;
      var testId = mEl.getAttribute('data-testid');
      if (testId !== 'increment' && testId !== 'decrement') return;
      function forwardToSource(e) {
        e.preventDefault();
        e.stopPropagation();
        var sourceRow = getSourceStepperRow();
        if (!sourceRow) return;
        var sEl = sourceRow.querySelector('[data-testid="' + testId + '"]');
        if (sEl) sEl.click();
        queueMirrorQtySyncFromSource(mirrorRow, getSourceStepperRow);
      }
      mEl.addEventListener('click', forwardToSource, true);
      mEl.addEventListener(
        'keydown',
        function (e) {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          forwardToSource(e);
        },
        true,
      );
    }
    bindQtyButton(mirrorRow.querySelector('[data-testid="increment"]'));
    bindQtyButton(mirrorRow.querySelector('[data-testid="decrement"]'));
    if (mInp) {
      mInp.addEventListener('input', function () {
        var sourceRow = getSourceStepperRow();
        var sInp = sourceRow && sourceRow.querySelector('input[type="number"]');
        if (!sInp) return;
        sInp.value = mInp.value;
        sInp.dispatchEvent(new Event('input', { bubbles: true }));
        sInp.dispatchEvent(new Event('change', { bubbles: true }));
      });
      mInp.addEventListener('change', function () {
        var sourceRow = getSourceStepperRow();
        var sInp = sourceRow && sourceRow.querySelector('input[type="number"]');
        if (!sInp) return;
        sInp.value = mInp.value;
        sInp.dispatchEvent(new Event('change', { bubbles: true }));
        queueMirrorQtySyncFromSource(mirrorRow, getSourceStepperRow);
      });
    }
  }

  /**
   * Match quickview h1 to `.recharge-heading-h3` in the bundle list, then click that card's primary Add.
   */
  function findBundleAddButtonByH3Title(container, rawTitle) {
    var m = findBestBundleCardByH3TitleInContainer(container, rawTitle);
    if (!m) return null;
    return firstNativeRechargePrimaryIn(m.card);
  }

  function findBundleAddButtonForQuickviewTitle(container, rawTitle) {
    return (
      findBundleAddButtonByH3Title(container, rawTitle) ||
      findBundleAddButtonByAriaTitle(container, rawTitle)
    );
  }

  function wireQuickviewAddToBundlePrimary(btn, modalRoot, priceSpan, column) {
    btn.onclick = function () {
      var title = getQuickviewHeadingTitle(modalRoot, priceSpan);
      var bundleBtn = title
        ? findBundleAddButtonAcrossBundleContainers(modalRoot, title)
        : null;
      if (bundleBtn && !bundleBtn.disabled) {
        bundleBtn.click();
        return;
      }
      var t = findRowAddTrigger(priceSpan);
      if (!t && column) t = firstNativeRechargePrimaryIn(column);
      if (t) {
        t.click();
      }
    };
  }

  /** Recharge replaces Add with increment/decrement + number input once the line is in the bundle. */
  function modalPriceColumnHasQuantityStepper(col) {
    if (!col || !col.querySelector) return false;
    if (
      col.querySelector('[data-testid="increment"]') ||
      col.querySelector('[data-testid="decrement"]')
    ) {
      return true;
    }
    var inputs = col.querySelectorAll('input[type="number"]');
    var i;
    var inp;
    for (i = 0; i < inputs.length; i++) {
      inp = inputs[i];
      if (inp.closest && inp.closest('.recharge-bundle-row-add-btn')) continue;
      return true;
    }
    return false;
  }

  function stripModalRowProxyAndShowNativePrice(col, nativeHost) {
    var i;
    if (col && col.querySelectorAll) {
      var extras = col.querySelectorAll('.recharge-bundle-row-add-btn');
      for (i = 0; i < extras.length; i++) extras[i].remove();
      var simpleAdds = col.querySelectorAll('.recharge-bundle-modal-simple-add-btn');
      for (i = 0; i < simpleAdds.length; i++) simpleAdds[i].remove();
      var mirrors = col.querySelectorAll('.recharge-bundle-modal-stepper-mirror');
      for (i = 0; i < mirrors.length; i++) mirrors[i].remove();
    }
    if (nativeHost && nativeHost.classList) {
      nativeHost.classList.remove('recharge-bundle-native-price-host');
    }
  }

  function stripModalStepperMirrorsInColumn(col) {
    if (!col || !col.querySelectorAll) return;
    var mirrors = col.querySelectorAll('.recharge-bundle-modal-stepper-mirror');
    var mi;
    for (mi = 0; mi < mirrors.length; mi++) {
      if (mirrors[mi]) mirrors[mi].remove();
    }
  }

  function stripModalThemeAddControlsInColumn(col) {
    if (!col || !col.querySelectorAll) return;
    var extras = col.querySelectorAll('.recharge-bundle-row-add-btn');
    var i;
    for (i = 0; i < extras.length; i++) extras[i].remove();
    var simple = col.querySelectorAll('.recharge-bundle-modal-simple-add-btn');
    for (i = 0; i < simple.length; i++) simple[i].remove();
  }

  /** Row-add proxies only; keep simple-add so enhance passes stay idempotent. */
  function stripModalRowAddProxyButtonsOnly(col) {
    if (!col || !col.querySelectorAll) return;
    var extras = col.querySelectorAll('.recharge-bundle-row-add-btn');
    var i;
    for (i = 0; i < extras.length; i++) extras[i].remove();
  }

  function enhanceModalRowAddButtons() {
    var modal = getRechargeModalRoot();
    if (!modal) return;
    var candidates = modal.querySelectorAll('span.recharge-text.recharge-text-p3-compact');
    if (!candidates.length) {
      candidates = modal.querySelectorAll('span.recharge-text-p3-compact');
    }
    for (var i = 0; i < candidates.length; i++) {
      var span = candidates[i];
      if (span.closest('.recharge-button-primary')) continue;
      var aria = span.getAttribute('aria-label') || '';
      if (!/actual price/i.test(aria)) continue;
      var priceStr = parseActualPriceLabel(span);
      if (!priceStr) continue;

      var nativeHost = span.closest('div._17o99wplo') || span.parentElement;
      var col = resolvePriceColumn(span);
      if (!col) continue;

      if (getLiveQuantityStepperRowInModalColumn(col)) {
        stripModalRowProxyAndShowNativePrice(col, nativeHost);
        continue;
      }

      var qvTitle = getQuickviewHeadingTitle(modal, span);
      var bundleCard = qvTitle
        ? findBestBundleCardForQuickviewTitle(modal, qvTitle)
        : null;
      var sourceStepperRow =
        bundleCard && getQuantityStepperRowFromCard(bundleCard);

      if (sourceStepperRow) {
        stripModalThemeAddControlsInColumn(col);
        if (nativeHost && nativeHost.classList) {
          nativeHost.classList.remove('recharge-bundle-native-price-host');
        }

        var mirrorTitleKey = qvTitle ? normalizeProductTitleForMatch(qvTitle) : '';
        var existingMirror =
          nativeHost &&
          nativeHost.nextElementSibling &&
          nativeHost.nextElementSibling.classList &&
          nativeHost.nextElementSibling.classList.contains(
            'recharge-bundle-modal-stepper-mirror',
          )
            ? nativeHost.nextElementSibling
            : col.querySelector('.recharge-bundle-modal-stepper-mirror');
        if (
          existingMirror &&
          col.contains(existingMirror) &&
          (existingMirror.getAttribute('data-ffm-mirror-for') || '') === mirrorTitleKey &&
          sourceStepperRow.isConnected
        ) {
          var innerKeep = existingMirror.firstElementChild;
          if (innerKeep) {
            var mKeep = innerKeep.querySelector('input[type="number"]');
            var sKeep = sourceStepperRow.querySelector('input[type="number"]');
            if (mKeep && sKeep) mKeep.value = sKeep.value;
          }
          continue;
        }

        stripModalStepperMirrorsInColumn(col);
        var wrap = document.createElement('div');
        wrap.className = 'recharge-bundle-modal-stepper-mirror w-100';
        wrap.setAttribute('data-ffm-mirror-for', mirrorTitleKey);
        var clone = sourceStepperRow.cloneNode(true);
        wrap.appendChild(clone);
        var mInp = clone.querySelector('input[type="number"]');
        var sInp = sourceStepperRow.querySelector('input[type="number"]');
        if (mInp && sInp) mInp.value = sInp.value;
        var getSourceStepperRow = function () {
          var t = getQuickviewHeadingTitle(modal, span);
          var c = t ? findBestBundleCardForQuickviewTitle(modal, t) : null;
          return c ? getQuantityStepperRowFromCard(c) : null;
        };
        wireModalStepperMirror(clone, getSourceStepperRow);
        if (nativeHost) {
          nativeHost.insertAdjacentElement('afterend', wrap);
        } else {
          col.appendChild(wrap);
        }
        continue;
      }

      stripModalStepperMirrorsInColumn(col);
      stripModalRowAddProxyButtonsOnly(col);

      if (nativeHost && nativeHost.classList) {
        nativeHost.classList.remove('recharge-bundle-native-price-host');
      }

      if (modalPriceColumnHasQuantityStepper(col)) {
        stripModalRowProxyAndShowNativePrice(col, nativeHost);
        continue;
      }

      var existingSimple =
        (nativeHost &&
          nativeHost.nextElementSibling &&
          nativeHost.nextElementSibling.classList &&
          nativeHost.nextElementSibling.classList.contains(
            'recharge-bundle-modal-simple-add-btn',
          ) &&
          nativeHost.nextElementSibling) ||
        col.querySelector('.recharge-bundle-modal-simple-add-btn');
      if (existingSimple) {
        existingSimple.textContent = 'Add';
        existingSimple.disabled = false;
        existingSimple.removeAttribute('title');
        wireQuickviewAddToBundlePrimary(existingSimple, modal, span, col);
        continue;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('tabindex', '0');
      btn.setAttribute('aria-label', 'Add');
      btn.className =
        'recharge-button recharge-button-primary recharge-bundle-modal-simple-add-btn w-100';
      btn.textContent = 'Add';
      wireQuickviewAddToBundlePrimary(btn, modal, span, col);
      if (nativeHost) {
        nativeHost.insertAdjacentElement('afterend', btn);
      } else {
        col.appendChild(btn);
      }
    }
  }

  function getBundleMutationObserveRoot() {
    if (getRechargeModalRoot()) {
      return document.body;
    }
    return getBundleObserveRoot();
  }

  var RECHARGE_MODAL_DESC_OUTER_SEL =
    '._15b7gxl0._17o99wpju._17o99wpx0._1jrh2c60';
  var RECHARGE_MODAL_DESC_INNER_SEL = '._17o99wpxn';
  var modalTabsHtmlByHandle = Object.create(null);
  var modalTabsFetchPromises = Object.create(null);

  function getProductHandleFromRechargeModal(modal) {
    if (!modal || !modal.querySelectorAll) return null;
    var links = modal.querySelectorAll('a[href*="/products/"]');
    var i;
    var href;
    var m;
    for (i = 0; i < links.length; i++) {
      href = links[i].getAttribute('href') || '';
      m = href.match(/\/products\/([^/?#]+)/);
      if (m) return decodeURIComponent(m[1]);
    }
    var h1 = modal.querySelector('.recharge-heading-h1');
    if (h1) {
      var rawTitle = stripLeadingProductIndexPrefix(h1.textContent || '');
      if (rawTitle) {
        var card = findBestBundleCardForQuickviewTitle(modal, rawTitle);
        if (card) {
          var fromCard = getProductHandleFromCard(card);
          if (fromCard) return fromCard;
        }
      }
    }
    var pidEl = modal.querySelector('[data-recharge-product-id]');
    var pid = pidEl && pidEl.getAttribute('data-recharge-product-id');
    if (pid && productIdToHandleCache[String(pid)]) {
      return productIdToHandleCache[String(pid)];
    }
    return null;
  }

  var RECHARGE_LEARN_MORE_MODAL_DESC_SEL = '.recharge-learn-more-modal-description';
  var RECHARGE_LEARN_MORE_MODAL_HEADER_SEL = '.recharge-learn-more-modal-header';

  /**
   * Recharge long description lives in ._17o99wpxn (older) or inside .recharge-learn-more-modal-description (newer).
   * Theme tabs replace that node’s native copy; header is moved into the same column above the tabs host.
   */
  function findRechargeModalDescInner(modal) {
    if (!modal || !modal.querySelector) return null;
    var lmDesc = modal.querySelector(RECHARGE_LEARN_MORE_MODAL_DESC_SEL);
    if (lmDesc) {
      var hashed = lmDesc.querySelector(RECHARGE_MODAL_DESC_INNER_SEL);
      if (hashed) return hashed;
      return lmDesc;
    }
    var outer = modal.querySelector(RECHARGE_MODAL_DESC_OUTER_SEL);
    if (outer) {
      var inner = outer.querySelector(RECHARGE_MODAL_DESC_INNER_SEL);
      if (inner) return inner;
    }
    return modal.querySelector(RECHARGE_MODAL_DESC_INNER_SEL);
  }

  /** Place Recharge “learn more” title block above theme tabs inside the description column. */
  function reconcileRechargeLearnMoreModalHeaderInDesc(modal, desc) {
    if (!modal || !desc || !modal.querySelector) return;
    var header = modal.querySelector(RECHARGE_LEARN_MORE_MODAL_HEADER_SEL);
    if (!header) return;
    if (!desc.contains(header)) {
      desc.insertBefore(header, desc.firstChild);
      return;
    }
    if (header.parentNode !== desc || desc.firstChild !== header) {
      desc.insertBefore(header, desc.firstChild);
    }
  }

  /**
   * Remove native Recharge copy from the desc cell, leaving only optional learn-more header + tabs host.
   * Must skip redundant replaceChildren when [header, host] is already correct — otherwise every
   * enhanceRechargeModalProductTabs pass (MutationObserver + debounced timers) re-mutates the modal
   * and can freeze the page (observer feedback with Recharge re-renders).
   */
  function consolidateRechargeModalDescChildren(desc, host, learnHeader) {
    if (!desc || !host) return;
    var kids = desc.children;
    var n = kids.length;
    if (learnHeader) {
      if (n === 2 && kids[0] === learnHeader && kids[1] === host) return;
      desc.replaceChildren(learnHeader, host);
      return;
    }
    if (n === 1 && kids[0] === host) return;
    desc.replaceChildren(host);
  }

  function parseModalTabsFetchPayload(text) {
    if (!text) return null;
    var t = text.replace(/^\uFEFF/, '').trim();
    var data;
    try {
      data = JSON.parse(t);
    } catch (e) {
      data = null;
    }
    if (data && typeof data.html === 'string') return data;
    var brace = t.indexOf('{"html":');
    if (brace !== -1) {
      try {
        data = JSON.parse(t.slice(brace));
        if (data && typeof data.html === 'string') return data;
      } catch (e2) {}
    }
    return null;
  }

  function fetchModalTabsContent(handle) {
    if (!handle) return Promise.resolve(null);
    if (modalTabsHtmlByHandle[handle]) return Promise.resolve(modalTabsHtmlByHandle[handle]);
    if (modalTabsFetchPromises[handle]) return modalTabsFetchPromises[handle];
    var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = shopRoot + 'products/' + encodeURIComponent(handle) + '?view=modal-tabs-json';
    modalTabsFetchPromises[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        var data = parseModalTabsFetchPayload(text);
        var html = data && data.html;
        if (html && typeof html === 'string') {
          modalTabsHtmlByHandle[handle] = html;
          return html;
        }
        return null;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        delete modalTabsFetchPromises[handle];
      });
    return modalTabsFetchPromises[handle];
  }

  function prefetchModalTabsForHandle(handle) {
    if (handle && !modalTabsHtmlByHandle[handle] && !modalTabsFetchPromises[handle]) {
      fetchModalTabsContent(handle);
    }
  }

  var MODAL_TABS_SKELETON_HTML =
    '<div class="recharge-modal-tabs-skeleton" aria-hidden="true">' +
    '<div class="recharge-modal-tabs-skeleton__tabs"></div>' +
    '<div class="recharge-modal-tabs-skeleton__body">' +
    '<div class="recharge-modal-tabs-skeleton__line"></div>' +
    '<div class="recharge-modal-tabs-skeleton__line recharge-modal-tabs-skeleton__line--mid"></div>' +
    '<div class="recharge-modal-tabs-skeleton__line recharge-modal-tabs-skeleton__line--short"></div>' +
    '</div></div>';

  function setModalTabsLoadingSkeleton(host) {
    if (!host || host.querySelector('.recharge-modal-tabs-skeleton')) return;
    host.innerHTML = MODAL_TABS_SKELETON_HTML;
  }

  function fadeInModalTabsRoot(host) {
    var root = host && host.querySelector('.recharge-modal-product-tabs-root');
    if (!root) return;
    root.classList.remove('ffm-modal-tabs-root--in');
    void root.offsetWidth;
    root.classList.add('ffm-modal-tabs-root--in');
  }

  var MODAL_WIDTH_LOCK_MIN_VIEWPORT_PX = 1200;
  var modalWidthLockMqlListenerSetup = false;

  function isModalWidthLockViewportAllowed() {
    return window.matchMedia(
      '(min-width: ' + MODAL_WIDTH_LOCK_MIN_VIEWPORT_PX + 'px)',
    ).matches;
  }

  function sweepClearModalWidthLocksIfViewportNarrow() {
    if (isModalWidthLockViewportAllowed()) return;
    var locked = document.querySelectorAll('[data-ffm-width-locked="1"]');
    var i;
    var el;
    var modal;
    for (i = 0; i < locked.length; i++) {
      el = locked[i];
      modal = el.closest('.recharge-modal-wrapper');
      if (!modal) modal = el.closest('[class*="recharge-modal-wrapper"]');
      if (modal) clearRechargeModalWidthLock(modal);
      else if (el.style) {
        el.style.width = '';
        el.style.minWidth = '';
        el.style.maxWidth = '';
        el.style.boxSizing = '';
        el.removeAttribute('data-ffm-width-locked');
      }
    }
  }

  function ensureModalWidthLockViewportListener() {
    if (modalWidthLockMqlListenerSetup) return;
    modalWidthLockMqlListenerSetup = true;
    var mql = window.matchMedia(
      '(min-width: ' + MODAL_WIDTH_LOCK_MIN_VIEWPORT_PX + 'px)',
    );
    function onViewportChange() {
      if (mql.matches) return;
      sweepClearModalWidthLocksIfViewportNarrow();
    }
    if (mql.addEventListener) {
      mql.addEventListener('change', onViewportChange);
    } else if (mql.addListener) {
      mql.addListener(onViewportChange);
    }
  }

  function clearRechargeModalWidthLock(modal) {
    if (!modal) return;
    var dialog = modal.querySelector('[role="dialog"]');
    [modal, dialog].forEach(function (el) {
      if (!el || !el.style) return;
      el.style.width = '';
      el.style.minWidth = '';
      el.style.maxWidth = '';
      el.style.boxSizing = '';
      el.removeAttribute('data-ffm-width-locked');
    });
  }

  /**
   * Close the Recharge bundle quickview. Trustpilot Q&A injects its own UI; we try native dialog, then
   * a labeled close control, then a synthetic Escape (common for app modals).
   */
  function closeRechargeQuickviewModal(modal) {
    if (!modal || !modal.isConnected) return;
    var dialog = modal.querySelector('[role="dialog"]');
    if (dialog && typeof dialog.close === 'function') {
      try {
        if (dialog.open) {
          dialog.close();
          return;
        }
      } catch (e) {}
    }
    var btns = modal.querySelectorAll('button');
    var i;
    var b;
    var al;
    for (i = 0; i < btns.length; i++) {
      b = btns[i];
      al = (b.getAttribute('aria-label') || '').trim();
      if (al && (/^close\b/i.test(al) || /^dismiss\b/i.test(al))) {
        b.click();
        return;
      }
    }
    var esc = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true,
      cancelable: true,
    });
    if (dialog) dialog.dispatchEvent(esc);
    modal.dispatchEvent(esc);
    document.body.dispatchEvent(esc);
  }

  var TRUSTPILOT_WIDGET_ORIGIN = 'https://widget.trustpilot.com';
  var trustpilotAskCloseRechargeSetup = false;

  function parseTrustpilotPostMessagePayload(data) {
    if (data == null) return null;
    if (typeof data === 'object' && data.command) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (err) {
        return null;
      }
    }
    return null;
  }

  function findRechargeModalForTrustpilotMessageSource(source) {
    if (!source) return null;
    var roots = document.querySelectorAll('.recharge-modal-product-tabs-root');
    var i;
    var j;
    var root;
    var iframes;
    var frame;
    for (i = 0; i < roots.length; i++) {
      root = roots[i];
      iframes = root.querySelectorAll('iframe');
      for (j = 0; j < iframes.length; j++) {
        frame = iframes[j];
        try {
          if (frame.contentWindow === source) {
            return (
              root.closest('.recharge-modal-wrapper') ||
              root.closest('[class*="recharge-modal-wrapper"]')
            );
          }
        } catch (err) {}
      }
    }
    return null;
  }

  function setupTrustpilotAskQuestionClosesRechargeModal() {
    if (trustpilotAskCloseRechargeSetup) return;
    trustpilotAskCloseRechargeSetup = true;
    window.addEventListener(
      'message',
      function (event) {
        if (!event || event.origin !== TRUSTPILOT_WIDGET_ORIGIN) return;
        var payload = parseTrustpilotPostMessagePayload(event.data);
        if (!payload || payload.command !== 'new-question') return;
        var modal = findRechargeModalForTrustpilotMessageSource(event.source);
        if (!modal) return;
        window.setTimeout(function () {
          closeRechargeQuickviewModal(modal);
        }, 0);
      },
      false,
    );
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || t.nodeType !== 1) return;
        var btn = t.closest('button[type="submit"]');
        if (!btn) return;
        if (!btn.closest('.recharge-modal-product-tabs-root')) return;
        var modal =
          btn.closest('.recharge-modal-wrapper') ||
          btn.closest('[class*="recharge-modal-wrapper"]');
        if (!modal) return;
        var label = (btn.textContent || '').replace(/\s+/g, ' ').trim();
        if (!/ask a question/i.test(label)) return;
        window.setTimeout(function () {
          closeRechargeQuickviewModal(modal);
        }, 0);
      },
      false,
    );
  }

  /**
   * After Details (first tab) is visible, pin modal/dialog width so other tabs cannot resize the shell.
   * Only at viewport >= 1200px (matches .recharge-modal-desc-tabs breakpoint in recharge-bundle.css).
   */
  function lockRechargeModalWidthToDetailsTab(modal) {
    ensureModalWidthLockViewportListener();
    if (!modal) return;
    if (!isModalWidthLockViewportAllowed()) {
      clearRechargeModalWidthLock(modal);
      return;
    }
    var shell = modal.querySelector('[role="dialog"]') || modal;
    if (shell.getAttribute('data-ffm-width-locked') === '1') return;

    function applyWidth(px) {
      if (!shell.isConnected || px < 160) return;
      var cap = Math.max(160, Math.floor(window.innerWidth - 24));
      var w = Math.min(Math.ceil(px), cap);
      shell.style.boxSizing = 'border-box';
      shell.style.width = w + 'px';
      shell.style.minWidth = w + 'px';
      shell.style.maxWidth = w + 'px';
      shell.setAttribute('data-ffm-width-locked', '1');
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var w0 = shell.getBoundingClientRect().width;
        if (w0 >= 160) applyWidth(w0);
        setTimeout(function () {
          if (!shell.isConnected) return;
          var wLate = shell.getBoundingClientRect().width;
          var current = parseInt(shell.style.width, 10) || 0;
          if (wLate >= 160 && (!current || wLate > current + 3)) {
            applyWidth(wLate);
          }
        }, 160);
      });
    });
  }

  function applyModalTabsHtmlToHost(host, expectedHandle, html) {
    if (!host || !html) return false;
    var activeModal = getRechargeModalRootForTabs();
    if (!activeModal || !activeModal.contains(host) || !host.isConnected) return false;
    if (getProductHandleFromRechargeModal(activeModal) !== expectedHandle) return false;
    host.classList.remove('recharge-modal-product-tabs-host--loading');
    host.innerHTML = html;
    host.setAttribute('data-loaded-handle', expectedHandle);
    activateScriptsInRechargeModalTabs(host);
    initRechargeModalFfmTabs(host);
    fadeInModalTabsRoot(host);
    lockRechargeModalWidthToDetailsTab(activeModal);
    return true;
  }

  function activateScriptsInRechargeModalTabs(container) {
    if (!container || !container.querySelectorAll) return;
    var scripts = Array.prototype.slice.call(container.querySelectorAll('script'));
    var i;
    var oldEl;
    var nu;
    var ai;
    for (i = 0; i < scripts.length; i++) {
      oldEl = scripts[i];
      nu = document.createElement('script');
      for (ai = 0; ai < oldEl.attributes.length; ai++) {
        nu.setAttribute(oldEl.attributes[ai].name, oldEl.attributes[ai].value);
      }
      nu.textContent = oldEl.textContent;
      oldEl.parentNode.replaceChild(nu, oldEl);
    }
  }

  function initRechargeModalFfmTabs(scope) {
    if (!scope || !scope.querySelector) return;
    var ts = scope.querySelector('.ffm_tablist--recharge-modal');
    if (!ts) return;
    var tabs = ts.querySelectorAll('.ffm_tab');
    var panels = ts.querySelectorAll('.ffm_tab_panel');
    if (!tabs.length || tabs.length !== panels.length) return;

    var j;
    for (j = 0; j < tabs.length; j++) {
      (function (idx) {
        tabs[idx].setAttribute('data-ffm-modal-index', String(idx));
        tabs[idx].onclick = function (e) {
          var index = parseInt(
            e.currentTarget.getAttribute('data-ffm-modal-index'),
            10,
          );
          var clickedTab = tabs[index];
          var clickedPanel = panels[index];
          var k;
          for (k = 0; k < tabs.length; k++) tabs[k].classList.remove('active');
          for (k = 0; k < panels.length; k++) panels[k].classList.remove('active');
          clickedTab.classList.add('active');
          clickedPanel.classList.add('active');
        };
      })(j);
    }
    if (tabs[0]) tabs[0].classList.add('active');
    if (panels[0]) panels[0].classList.add('active');
  }

  function ensureRechargeModalTabsHost(modal) {
    if (!modal || !modal.querySelector) return null;
    var desc = findRechargeModalDescInner(modal);
    var host;
    if (desc) {
      reconcileRechargeLearnMoreModalHeaderInDesc(modal, desc);
      host = desc.querySelector(':scope > .recharge-modal-product-tabs-host');
      var learnHeader = desc.querySelector(':scope > ' + RECHARGE_LEARN_MORE_MODAL_HEADER_SEL);
      if (host) {
        consolidateRechargeModalDescChildren(desc, host, learnHeader);
      }
      if (!host) {
        learnHeader = desc.querySelector(':scope > ' + RECHARGE_LEARN_MORE_MODAL_HEADER_SEL);
        desc.replaceChildren();
        if (learnHeader) desc.appendChild(learnHeader);
        host = document.createElement('div');
        host.className = 'recharge-modal-product-tabs-host';
        desc.appendChild(host);
      }
      desc.classList.add('recharge-modal-desc-tabs');
      return host;
    }
    var mount =
      modal.querySelector(RECHARGE_MODAL_DESC_OUTER_SEL) ||
      (function () {
        var loose = modal.querySelector(RECHARGE_MODAL_DESC_INNER_SEL);
        return loose && loose.parentElement ? loose.parentElement : null;
      })();
    if (!mount) return null;
    host = mount.querySelector('.recharge-modal-product-tabs-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'recharge-modal-product-tabs-host';
      mount.insertBefore(host, mount.firstChild);
    }
    return host;
  }

  function enhanceRechargeModalProductTabs() {
    var modal = getRechargeModalRootForTabs();
    if (!modal) return;

    var handle = getProductHandleFromRechargeModal(modal);
    if (!handle) {
      var pidEl = modal.querySelector('[data-recharge-product-id]');
      var pid = pidEl && pidEl.getAttribute('data-recharge-product-id');
      if (pid) {
        fetchProductHandleById(pid).then(function (h) {
          if (h) enhanceRechargeModalProductTabs();
        });
      }
      return;
    }

    var host = ensureRechargeModalTabsHost(modal);
    if (!host) return;

    var loadedFor = host.getAttribute('data-loaded-handle');
    if (loadedFor === handle) return;

    if (loadedFor && loadedFor !== handle) {
      clearRechargeModalWidthLock(modal);
      host.removeAttribute('data-loaded-handle');
      host.classList.remove('recharge-modal-product-tabs-host--loading');
      host.innerHTML = '';
    }

    var cached = modalTabsHtmlByHandle[handle];
    if (cached && applyModalTabsHtmlToHost(host, handle, cached)) return;

    host.classList.add('recharge-modal-product-tabs-host--loading');
    setModalTabsLoadingSkeleton(host);

    fetchModalTabsContent(handle).then(function (html) {
      if (applyModalTabsHtmlToHost(host, handle, html)) return;
      host.classList.remove('recharge-modal-product-tabs-host--loading');
      if (!html) host.innerHTML = '';
    });
  }

  function observeBundleWidget() {
    var observeRoot = getBundleMutationObserveRoot();
    if (!observeRoot) return;

    var refreshTimeout;
    function onBundleMutations(mutations) {
      var modalRoot = getRechargeModalRoot();
      var modalHit = false;
      if (modalRoot && mutations && mutations.length) {
        var mi;
        var t;
        for (mi = 0; mi < mutations.length; mi++) {
          t = mutations[mi].target;
          if (t && t.nodeType === 3 && t.parentElement) t = t.parentElement;
          if (t && modalRoot.contains(t)) {
            modalHit = true;
            break;
          }
        }
      }
      if (modalHit) {
        requestAnimationFrame(function () {
          enhanceRechargeModalProductTabs();
        });
      }
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(function () {
        refreshFromBundle();
        enhanceModalRowAddButtons();
        enhanceRechargeModalProductTabs();
        appendProductSizeInfo();
      }, 100);
    }

    new MutationObserver(onBundleMutations).observe(observeRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true,
    });

    refreshFromBundle();
    enhanceModalRowAddButtons();
    enhanceRechargeModalProductTabs();
    appendProductSizeInfo();
  }

  function setupOnAddToCart() {
    window.RechargeBundle = window.RechargeBundle || {};
    const originalOnAddToCart = window.RechargeBundle.onAddToCart;

    window.RechargeBundle.onAddToCart = function () {
      try {
        if (typeof originalOnAddToCart === 'function') {
          originalOnAddToCart.apply(this, arguments);
        }
      } catch (e) {}
      window.location.href = '/checkout';
    };
  }

  function getProductHandleFromCard(cardEl) {
    var link = cardEl && cardEl.querySelector('a[href*="/products/"]');
    if (link) {
      var match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    }
    var productId = cardEl && cardEl.getAttribute && cardEl.getAttribute('data-recharge-product-id');
    if (productId && productIdToHandleCache[productId]) return productIdToHandleCache[productId];
    return null;
  }

  function fetchProductHandleById(productId) {
    return ensureProductsCache().then(function () {
      return productIdToHandleCache[productId] || null;
    });
  }

  var RECHARGE_SIZE_HEADING_SEL =
    '.recharge-heading-h3, .recharge-heading.recharge-heading-h3, [role="heading"][aria-level="3"].recharge-heading,' +
    ' .recharge-heading-h1, .recharge-heading.recharge-heading-h1, [role="heading"][aria-level="1"].recharge-heading';

  var SIZE_INFO_DEBOUNCE_MS = 150;
  var SIZE_INFO_PREFETCH_STAGGER_MS = 40;
  /** Recharge often paints bundle headings after first paint; re-run append until rows exist. */
  var SIZE_INFO_PAGE_LOAD_RETRY_MS = [0, 50, 120, 250, 500, 900, 1400, 2200, 3200];

  var sizeInfoInnerHtmlByHandle = Object.create(null);
  var sizeInfoFetchPromises = Object.create(null);
  var sizeInfoPrefetchPointerDone = false;
  var modalTabsHoverPrefetchSetup = false;
  var modalTabsViewportPrefetchSetup = false;
  var modalTabsHoverPrefetchOnce = Object.create(null);
  var sizeInfoAppendSetup = false;

  function parseSizeInfoJsonToContent(text) {
    try {
      var data = JSON.parse(text);
      var html = (data && data.html && String(data.html).trim()) || '';
      if (!html) return null;
      var w = document.createElement('div');
      w.innerHTML = html;
      // Prefer the multi-variant wrapper so all VariantSize metafield blocks stay intact.
      var inner =
        w.querySelector('.product-size-info-variants') ||
        w.querySelector('.product-size-info-section') ||
        w.querySelector('.product-size-information') ||
        w.firstElementChild ||
        w;
      var content = (inner.outerHTML || inner.innerHTML || '').trim();
      return content || null;
    } catch (e) {
      return null;
    }
  }

  function variantIdFromHref(href) {
    if (!href) return null;
    var m = String(href).match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  function getVariantIdFromEl(el) {
    if (!el) return null;
    if (el.getAttribute) {
      var direct = el.getAttribute('data-recharge-variant-id');
      if (direct) return String(direct);
    }
    var marked = el.querySelector && el.querySelector('[data-recharge-variant-id]');
    if (marked) {
      var fromMarked = marked.getAttribute('data-recharge-variant-id');
      if (fromMarked) return String(fromMarked);
    }
    var links = el.querySelectorAll ? el.querySelectorAll('a[href*="variant="]') : [];
    var li;
    for (li = 0; li < links.length; li++) {
      var fromHref = variantIdFromHref(links[li].getAttribute('href'));
      if (fromHref) return fromHref;
    }
    return null;
  }

  function resolveSelectedVariantIdForHeading(heading) {
    if (!heading) return null;
    var card =
      heading.closest('.recharge-bundle-product-item') ||
      heading.closest('[data-recharge-product-id]') ||
      heading.closest('[data-recharge-variant-id]') ||
      heading.closest('.recharge-card') ||
      heading.closest('[id*="recharge"]');
    var fromCard = getVariantIdFromEl(card);
    if (fromCard) return fromCard;

    var modalRoot =
      heading.closest('.recharge-modal-wrapper') || heading.closest('[class*="recharge-modal-wrapper"]');
    if (modalRoot && modalRoot.contains(heading)) {
      var fromModal = getVariantIdFromEl(modalRoot);
      if (fromModal) return fromModal;
      var listCard = findBestBundleCardForQuickviewTitle(
        modalRoot,
        stripLeadingProductIndexPrefix(heading.textContent || ''),
      );
      var fromList = getVariantIdFromEl(listCard);
      if (fromList) return fromList;
    }
    return null;
  }

  function syncInjectedSizeInfoVisibility(injectedRoot, variantId) {
    if (!injectedRoot) return;
    var fields = injectedRoot.querySelectorAll('.__ffm_variant_field[data-variant]');
    if (!fields.length) return;
    if (!variantId) return;
    var vid = String(variantId);
    var i;
    var el;
    var display;
    for (i = 0; i < fields.length; i++) {
      el = fields[i];
      display = el.getAttribute('data-display') || 'block';
      el.style.display = String(el.getAttribute('data-variant')) === vid ? display : 'none';
    }
  }

  function syncSizeInfoForHeading(heading) {
    if (!heading || !heading.isConnected) return;
    var next = heading.nextElementSibling;
    if (!next || !next.classList || !next.classList.contains('product-size-info-injected')) return;
    syncInjectedSizeInfoVisibility(next, resolveSelectedVariantIdForHeading(heading));
  }

  function syncAllInjectedSizeInfoVisibility() {
    collectSizeInfoHeadings().forEach(syncSizeInfoForHeading);
  }

  function fetchSizeInfoContent(handle) {
    if (!handle) return Promise.resolve(null);
    if (sizeInfoInnerHtmlByHandle[handle]) return Promise.resolve(sizeInfoInnerHtmlByHandle[handle]);
    if (sizeInfoFetchPromises[handle]) return sizeInfoFetchPromises[handle];
    var url = (window.Shopify?.routes?.root || '/') + 'products/' + handle + '?view=size-info-json';
    sizeInfoFetchPromises[handle] = fetch(url)
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        var content = parseSizeInfoJsonToContent(text);
        if (content) sizeInfoInnerHtmlByHandle[handle] = content;
        return content;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        delete sizeInfoFetchPromises[handle];
      });
    return sizeInfoFetchPromises[handle];
  }

  function prefetchSizeInfoForHandle(handle) {
    if (handle && !sizeInfoInnerHtmlByHandle[handle] && !sizeInfoFetchPromises[handle]) {
      fetchSizeInfoContent(handle);
    }
  }

  function injectSizeInfoDom(container, content) {
    if (!container || !content) return false;
    var next = container.nextElementSibling;
    var injected;
    if (next && next.classList && next.classList.contains('product-size-info-injected')) {
      injected = next;
      // Keep markup current (e.g. after all-variant JSON replaces a single-variant cache hit).
      if (injected.innerHTML !== content) injected.innerHTML = content;
    } else {
      injected = document.createElement('div');
      injected.className = 'product-size-info-injected mt-2 text-center';
      injected.innerHTML = content;
      container.insertAdjacentElement('afterend', injected);
    }
    syncInjectedSizeInfoVisibility(injected, resolveSelectedVariantIdForHeading(container));
    return true;
  }

  function collectBundleProductHandlesForPrefetch() {
    var root = getBundleObserveRoot();
    if (!root || !root.querySelectorAll) return [];
    var seen = Object.create(null);
    var out = [];
    root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      var h = m && m[1];
      if (h && !seen[h]) {
        seen[h] = true;
        out.push(h);
      }
    });
    return out;
  }

  function schedulePrefetchAllBundleSizeInfo() {
    var handles = collectBundleProductHandlesForPrefetch();
    handles.forEach(function (handle, i) {
      setTimeout(function () {
        prefetchSizeInfoForHandle(handle);
      }, i * SIZE_INFO_PREFETCH_STAGGER_MS);
    });
    handles.forEach(function (handle) {
      prefetchModalTabsForHandle(handle);
    });
  }

  function setupSizeInfoPrefetchOnPointer() {
    if (sizeInfoPrefetchPointerDone) return;
    sizeInfoPrefetchPointerDone = true;
    document.addEventListener(
      'pointerdown',
      function (e) {
        var br = getBundleObserveRoot();
        if (!br || !br.contains(e.target)) return;
        var row = e.target.closest('.recharge-bundle-product-item, [data-recharge-product-id]');
        if (!row || !br.contains(row)) return;
        var rowHandle = getProductHandleFromCard(row);
        prefetchSizeInfoForHandle(rowHandle);
        prefetchModalTabsForHandle(rowHandle);
      },
      true,
    );
  }

  function setupModalTabsPrefetchOnHover() {
    if (modalTabsHoverPrefetchSetup) return;
    var br = getBundleObserveRoot();
    if (!br) return;
    modalTabsHoverPrefetchSetup = true;
    br.addEventListener(
      'mouseover',
      function (e) {
        if (!br.contains(e.target)) return;
        var row = e.target.closest('.recharge-bundle-product-item, [data-recharge-product-id]');
        if (!row || !br.contains(row)) return;
        var h = getProductHandleFromCard(row);
        if (!h || modalTabsHoverPrefetchOnce[h]) return;
        modalTabsHoverPrefetchOnce[h] = true;
        prefetchModalTabsForHandle(h);
        prefetchSizeInfoForHandle(h);
      },
      true,
    );
  }

  function setupModalTabsPrefetchOnViewport() {
    if (modalTabsViewportPrefetchSetup) return;
    var br = getBundleObserveRoot();
    if (!br || typeof IntersectionObserver === 'undefined') return;
    modalTabsViewportPrefetchSetup = true;

    function observeProductRow(el) {
      if (!el || el.nodeType !== 1 || el.__ffmModalTabsViewportObserved) return;
      el.__ffmModalTabsViewportObserved = true;
      io.observe(el);
    }

    var io = new IntersectionObserver(
      function (entries) {
        var ei;
        for (ei = 0; ei < entries.length; ei++) {
          if (!entries[ei].isIntersecting) continue;
          var row = entries[ei].target;
          var h = getProductHandleFromCard(row);
          if (!h || modalTabsHoverPrefetchOnce[h]) continue;
          modalTabsHoverPrefetchOnce[h] = true;
          prefetchModalTabsForHandle(h);
          prefetchSizeInfoForHandle(h);
        }
      },
      { root: null, rootMargin: '200px 0px 280px 0px', threshold: 0.01 },
    );

    br.querySelectorAll('.recharge-bundle-product-item, [data-recharge-product-id]').forEach(observeProductRow);

    new MutationObserver(function (mutations) {
      var mi;
      var m;
      var ni;
      var node;
      for (mi = 0; mi < mutations.length; mi++) {
        m = mutations[mi];
        for (ni = 0; ni < m.addedNodes.length; ni++) {
          node = m.addedNodes[ni];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('.recharge-bundle-product-item, [data-recharge-product-id]')) {
            observeProductRow(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('.recharge-bundle-product-item, [data-recharge-product-id]').forEach(observeProductRow);
          }
        }
      }
    }).observe(br, { childList: true, subtree: true });
  }

  function collectSizeInfoHeadings() {
    var seen = new Set();
    var list = [];
    getSizeInfoSearchRoots().forEach(function (root) {
      if (!root || !root.querySelectorAll) return;
      root.querySelectorAll(RECHARGE_SIZE_HEADING_SEL).forEach(function (h) {
        if (!seen.has(h)) {
          seen.add(h);
          list.push(h);
        }
      });
    });
    return list;
  }

  function resolveSizeInfoHandle(heading) {
    var card =
      heading.closest('.recharge-bundle-product-item') ||
      heading.closest('[data-recharge-product-id]') ||
      heading.closest('.recharge-card') ||
      heading.closest('[id*="recharge"]');
    var handle = getProductHandleFromCard(card);
    var productId = card && card.getAttribute && card.getAttribute('data-recharge-product-id');
    if (!handle && !productId) {
      var modalRoot =
        heading.closest('.recharge-modal-wrapper') || heading.closest('[class*="recharge-modal-wrapper"]');
      if (modalRoot && modalRoot.contains(heading)) {
        var listCard = findBestBundleCardForQuickviewTitle(
          modalRoot,
          stripLeadingProductIndexPrefix(heading.textContent || ''),
        );
        if (listCard) {
          handle = getProductHandleFromCard(listCard);
          if (!handle && listCard.getAttribute) productId = listCard.getAttribute('data-recharge-product-id');
        }
      }
    }
    return { handle: handle, productId: productId };
  }

  function injectSizeInfo(anchorEl, handle) {
    if (!handle) return Promise.resolve();
    var cached = sizeInfoInnerHtmlByHandle[handle];
    if (cached && anchorEl && anchorEl.isConnected) {
      injectSizeInfoDom(anchorEl, cached);
      anchorEl.setAttribute('data-size-info-appended', 'true');
      return Promise.resolve();
    }
    anchorEl.setAttribute('data-size-info-appended', 'processing');
    return fetchSizeInfoContent(handle)
      .then(function (content) {
        if (content && anchorEl && anchorEl.isConnected) injectSizeInfoDom(anchorEl, content);
      })
      .catch(function () {})
      .finally(function () {
        if (anchorEl) anchorEl.setAttribute('data-size-info-appended', 'true');
      });
  }

  function appendProductSizeInfo() {
    var promises = [];
    collectSizeInfoHeadings().forEach(function (heading) {
      if (!heading) return;

      // Already injected: keep VariantSize metafields in sync with the selected variant.
      // If Recharge re-rendered the heading and dropped our sibling, clear the flag and re-inject.
      if (heading.getAttribute('data-size-info-appended')) {
        var existing = heading.nextElementSibling;
        if (existing && existing.classList && existing.classList.contains('product-size-info-injected')) {
          syncSizeInfoForHeading(heading);
          return;
        }
        heading.removeAttribute('data-size-info-appended');
      }

      var resolved = resolveSizeInfoHandle(heading);
      var handle = resolved.handle;
      var productId = resolved.productId;

      if (!handle && productId) {
        heading.setAttribute('data-size-info-appended', 'processing');
        promises.push(
          fetchProductHandleById(productId).then(function (h) {
            if (h) {
              productIdToHandleCache[productId] = h;
              return injectSizeInfo(heading, h);
            }
            heading.setAttribute('data-size-info-appended', 'true');
          }),
        );
        return;
      }
      if (!handle) return;

      heading.setAttribute('data-size-info-appended', 'processing');
      promises.push(injectSizeInfo(heading, handle));
    });
    if (promises.length) Promise.all(promises);
  }

  /**
   * Recharge subscription frequency modal: normalize option labels and dialog title.
   * Title default: "How often would you like to receive {bundle name}?" → "Deliver every:"
   * Options: strip leading "Every " and trailing ": save N%"; bare "week" → "1 week" (DOM + WeakSet + MutationObserver).
   * Light DOM: full document. Open shadow: under bundle host only.
   */
  var frequencyLabelTrimSetup = false;
  var MATCH_FREQ_SAVE = /[:\uFF1A]\s*save\s*[\d.,]+%/i;
  var MATCH_FREQ_LEADING_EVERY = /^\s*every\s+/i;
  var MATCH_FREQ_DIALOG_TITLE = /^\s*how\s+often\s+would\s+you\s+like\s+to\s+receive\b/i;
  var FREQ_SPAN = 'span[class*="recharge-text-p3"]';
  var FREQ_HEADING =
    '.recharge-heading-h3, .recharge-heading.recharge-heading-h3, [role="heading"][aria-level="3"].recharge-heading';

  function frequencyNormalize(s) {
    if (!s) {
      return '';
    }
    return String(s)
      .replace(/[\r\n\t\f\v]+/g, ' ')
      .replace(/[\s\u00a0]+/g, ' ')
      .trim();
  }

  function frequencyIsOneTimeLabel(text) {
    if (text == null || text === '') {
      return false;
    }
    var cleaned = frequencyNormalize(text).replace(MATCH_FREQ_SAVE, '').trim();
    return /^one[\s-]*time(?:\s+only|\s+purchase)?$/i.test(cleaned);
  }

  function frequencyNewTextWithoutSavePct(text) {
    if (text == null || text === '') {
      return null;
    }
    var one = frequencyNormalize(text);
    if (one.length === 0) {
      return null;
    }
    if (frequencyIsOneTimeLabel(one)) {
      return /^one-time purchase$/i.test(one.replace(MATCH_FREQ_SAVE, '').trim())
        ? null
        : 'One-time purchase';
    }
    var two = one.replace(MATCH_FREQ_SAVE, '').trim();
    two = two.replace(MATCH_FREQ_LEADING_EVERY, '').trim();
    if (two.length === 0) {
      return null;
    }
    if (/^week$/i.test(two)) {
      two = '1 week';
    }
    if (/^frequency$/i.test(two) || MATCH_FREQ_DIALOG_TITLE.test(two)) {
      return 'Deliver every:';
    }
    if (two === one) {
      return null;
    }
    return two;
  }

  function frequencyCollectEls(root, selector, out, seen) {
    if (root == null || root.querySelectorAll == null) {
      return;
    }
    var nodes = root.querySelectorAll(selector);
    var j;
    for (j = 0; j < nodes.length; j++) {
      var node = nodes[j];
      if (node == null || node.nodeType !== 1) {
        continue;
      }
      if (seen != null) {
        if (seen.has(node)) {
          continue;
        }
        seen.add(node);
      }
      out.push(node);
    }
    var walk = root.querySelectorAll('*');
    for (j = 0; j < walk.length; j++) {
      if (walk[j].shadowRoot) {
        frequencyCollectEls(walk[j].shadowRoot, selector, out, seen);
      }
    }
  }

  function frequencyScopedRoots() {
    var roots = [document];
    var sub =
      getBundleObserveRoot() ||
      document.getElementById('recharge-storefront-bundling-widget') ||
      document.getElementById('recharge-bundles');
    if (sub && roots.indexOf(sub) === -1) {
      roots.push(sub);
    }
    return roots;
  }

  function frequencyListSpanEls() {
    var out = [];
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
    var roots = frequencyScopedRoots();
    var r;
    for (r = 0; r < roots.length; r++) {
      frequencyCollectEls(roots[r], FREQ_SPAN, out, seen);
    }
    return out;
  }

  function frequencyDialogHeadingEls() {
    var out = [];
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
    var roots = frequencyScopedRoots();
    var r;
    for (r = 0; r < roots.length; r++) {
      frequencyCollectEls(roots[r], FREQ_HEADING, out, seen);
    }
    return out;
  }

  function reorderFrequencyOneTimeOptions() {
    var roots = frequencyScopedRoots();
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
    var listboxes = [];
    var r;
    var i;
    var j;

    for (r = 0; r < roots.length; r++) {
      frequencyCollectEls(roots[r], 'ul[role="listbox"]', listboxes, seen);
    }

    for (i = 0; i < listboxes.length; i++) {
      var listbox = listboxes[i];
      var options = listbox.querySelectorAll('[role="option"]');
      var oneTimeOption = null;
      if (!options.length) {
        continue;
      }
      for (j = 0; j < options.length; j++) {
        var option = options[j];
        var label =
          (option.querySelector && option.querySelector(FREQ_SPAN)) || option;
        if (frequencyIsOneTimeLabel(label.textContent)) {
          oneTimeOption = option;
          break;
        }
      }
      if (oneTimeOption && oneTimeOption !== options[options.length - 1]) {
        listbox.appendChild(oneTimeOption);
      }
    }
  }

  function trimRechargeFrequencyLabels() {
    if (!document.body) {
      return;
    }
    var i;
    var el;
    var n;
    var spans = frequencyListSpanEls();
    for (i = 0; i < spans.length; i++) {
      el = spans[i];
      n = frequencyNewTextWithoutSavePct(el.textContent);
      if (n != null) {
        el.textContent = n;
      }
    }
    var headings = frequencyDialogHeadingEls();
    for (i = 0; i < headings.length; i++) {
      el = headings[i];
      n = frequencyNewTextWithoutSavePct(el.textContent);
      if (n != null) {
        el.textContent = n;
      }
    }
    var al = document.querySelectorAll('[aria-label]');
    for (i = 0; i < al.length; i++) {
      el = al[i];
      n = frequencyNewTextWithoutSavePct(el.getAttribute('aria-label') || '');
      if (n != null) {
        el.setAttribute('aria-label', n);
      }
    }
    reorderFrequencyOneTimeOptions();
  }

  function setupFrequencyLabelTrim() {
    if (frequencyLabelTrimSetup) {
      return;
    }
    if (document.body.dataset.suffix !== 'recharge-bundle') {
      return;
    }
    frequencyLabelTrimSetup = true;

    var frequencyTrimRafId = null;
    function onTreeChange() {
      // Trim synchronously: MutationObserver runs before the next paint; deferring with setTimeout caused
      // a visible flash (Recharge briefly renders "Frequency" before macrotask trim ran).
      trimRechargeFrequencyLabels();
      if (frequencyTrimRafId == null && window.requestAnimationFrame) {
        frequencyTrimRafId = window.requestAnimationFrame(function () {
          frequencyTrimRafId = null;
          trimRechargeFrequencyLabels();
        });
      }
    }

    trimRechargeFrequencyLabels();
    window.setTimeout(trimRechargeFrequencyLabels, 200);
    window.setTimeout(trimRechargeFrequencyLabels, 1000);
    window.setTimeout(trimRechargeFrequencyLabels, 5000);

    var count = 0;
    var poll = window.setInterval(function () {
      trimRechargeFrequencyLabels();
      count = count + 1;
      if (count >= 40) {
        window.clearInterval(poll);
      }
    }, 400);

    var mo = new MutationObserver(onTreeChange);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true,
      attributes: true,
      attributeFilter: ['aria-label', 'class', 'style', 'hidden'],
    });
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(trimRechargeFrequencyLabels);
    }
  }

  function setupProductSizeInfoAppend() {
    if (sizeInfoAppendSetup) return;
    sizeInfoAppendSetup = true;

    /**
     * Quickview is often portaled under `body` outside `#recharge-storefront-bundling-widget`, so observing only
     * `getBundleObserveRoot()` misses modal mutations. `getRechargeModalRoot()` is also only the first wrapper
     * (often a hidden shell). Observe `body` plus every modal node so opens / re-renders still run append passes.
     */
    var observeRoot = document.body;

    var sizeInfoObserveOpts = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-recharge-variant-id', 'href', 'aria-selected', 'value'],
    };

    function ensureRechargeModalsObservedForSizeInfo(observer) {
      var modals = document.querySelectorAll(
        '.recharge-modal-wrapper, [class*="recharge-modal-wrapper"]',
      );
      var i;
      var el;
      for (i = 0; i < modals.length; i++) {
        el = modals[i];
        if (!el || el.__ffmRechargeSizeInfoObserved) continue;
        el.__ffmRechargeSizeInfoObserved = true;
        observer.observe(el, sizeInfoObserveOpts);
      }
    }

    function mutationTargetInAnyRechargeModal(rec) {
      var t = rec.target;
      if (t && t.nodeType === 3 && t.parentElement) t = t.parentElement;
      return !!(
        t &&
        t.closest &&
        t.closest('.recharge-modal-wrapper, [class*="recharge-modal-wrapper"]')
      );
    }

    function mutationLooksLikeVariantChange(rec) {
      if (!rec || rec.type !== 'attributes') return false;
      var name = rec.attributeName;
      return (
        name === 'data-recharge-variant-id' ||
        name === 'href' ||
        name === 'aria-selected' ||
        name === 'value'
      );
    }

    function runSizeInfoPass(observer) {
      ensureRechargeModalsObservedForSizeInfo(observer);
      appendProductSizeInfo();
      enhanceRechargeModalProductTabs();
    }

    function runModalTabsPassImmediate() {
      requestAnimationFrame(function () {
        enhanceRechargeModalProductTabs();
        requestAnimationFrame(function () {
          enhanceRechargeModalProductTabs();
        });
      });
    }

    function mutationTargetInBundleWidget(rec) {
      var t = rec.target;
      if (t && t.nodeType === 3 && t.parentElement) t = t.parentElement;
      var br = getBundleObserveRoot();
      return !!(br && t && br.contains(t));
    }

    var sizeInfoTimeout;
    var sizeInfoObserver = new MutationObserver(function (records) {
      var modalRelated = records.some(mutationTargetInAnyRechargeModal);
      var variantRelated = records.some(mutationLooksLikeVariantChange);
      if (modalRelated) {
        runSizeInfoPass(sizeInfoObserver);
        runModalTabsPassImmediate();
      }
      if (variantRelated) {
        syncAllInjectedSizeInfoVisibility();
      }
      if (modalRelated || variantRelated || records.some(mutationTargetInBundleWidget)) {
        clearTimeout(sizeInfoTimeout);
        sizeInfoTimeout = setTimeout(function () {
          runSizeInfoPass(sizeInfoObserver);
        }, SIZE_INFO_DEBOUNCE_MS);
      }
    });

    ensureRechargeModalsObservedForSizeInfo(sizeInfoObserver);
    sizeInfoObserver.observe(observeRoot, sizeInfoObserveOpts);
    // Catch Recharge variant selects even when data-recharge-variant-id is slow to update.
    document.addEventListener(
      'change',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (
          !t.closest('.recharge-bundle-product-item, [data-recharge-product-id], .recharge-modal-wrapper, [class*="recharge-modal-wrapper"], .recharge-bundle-variants')
        ) {
          return;
        }
        setTimeout(syncAllInjectedSizeInfoVisibility, 0);
        setTimeout(syncAllInjectedSizeInfoVisibility, 120);
      },
      true,
    );
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (
          !t.closest(
            '.recharge-bundle-variants, [data-recharge-variant-id], .recharge-select, [role="option"], [role="listbox"]',
          )
        ) {
          return;
        }
        if (
          !t.closest(
            '.recharge-bundle-product-item, [data-recharge-product-id], .recharge-modal-wrapper, [class*="recharge-modal-wrapper"], .recharge-bundle-variants',
          )
        ) {
          return;
        }
        setTimeout(syncAllInjectedSizeInfoVisibility, 0);
        setTimeout(syncAllInjectedSizeInfoVisibility, 120);
      },
      true,
    );
    setupSizeInfoPrefetchOnPointer();
    setupModalTabsPrefetchOnHover();
    setupModalTabsPrefetchOnViewport();

    schedulePrefetchAllBundleSizeInfo();
    runSizeInfoPass(sizeInfoObserver);
    requestAnimationFrame(function () {
      runSizeInfoPass(sizeInfoObserver);
    });
    SIZE_INFO_PAGE_LOAD_RETRY_MS.forEach(function (ms) {
      setTimeout(function () {
        runSizeInfoPass(sizeInfoObserver);
        if (ms === 500 || ms === 2200) schedulePrefetchAllBundleSizeInfo();
      }, ms);
    });

    function onWindowLoad() {
      schedulePrefetchAllBundleSizeInfo();
      runSizeInfoPass(sizeInfoObserver);
      [450, 1100].forEach(function (ms) {
        setTimeout(function () {
          runSizeInfoPass(sizeInfoObserver);
          schedulePrefetchAllBundleSizeInfo();
        }, ms);
      });
    }

    if (document.readyState === 'complete') {
      onWindowLoad();
    } else {
      window.addEventListener('load', onWindowLoad);
    }
  }

  const MAX_INIT_RETRIES = 20;

  function init(retryCount) {
    retryCount = retryCount || 0;
    var hasWidget =
      document.querySelector('.recharge-card') ||
      document.querySelector('.recharge-bundle-collections') ||
      document.querySelector('#recharge-bundles') ||
      document.querySelector('.recharge-modal-wrapper') ||
      document.querySelector('[class*="recharge-modal-wrapper"]');

    ensureProductsCache();

    if (!hasWidget && retryCount < MAX_INIT_RETRIES) {
      setTimeout(function () { init(retryCount + 1); }, 300);
      return;
    }

    setupOnAddToCart();
    if (hasWidget) {
      observeBundleWidget();
      scheduleDisclaimerPlacementRetries();
      setupProductSizeInfoAppend();
    }
    /* Do not require Recharge root nodes to exist: frequency UI can mount late or under different containers. */
    setupFrequencyLabelTrim();
  }

  function runInit() {
    setupRechargeBundleStickyTop();
    setupTrustpilotAskQuestionClosesRechargeModal();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    runInit();
  }

  function scheduleDelayedRefreshes() {
    [300, 800, 1500, 2500].forEach(function (delay) {
      setTimeout(function () {
        refreshFromBundle();
        enhanceModalRowAddButtons();
        enhanceRechargeModalProductTabs();
      }, delay);
    });
  }

  window.addEventListener('pageshow', function (event) {
    var alreadyReloaded = sessionStorage.getItem('rb_checkout_reload');
    if (alreadyReloaded) {
      sessionStorage.removeItem('rb_checkout_reload');
    }
    scheduleDelayedRefreshes();
  });

  function fetchAndRefreshBar() {
    var url = window.location.pathname + (window.location.search || '') + (window.location.search ? '&' : '?') + '_t=' + Date.now();
    fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var m = html.match(/data-cart-frozen-total=["']?(\d+)/);
        if (m) { cartFrozenTotal = Math.max(0, parseInt(m[1], 10) || 0); }
        m = html.match(/data-shipping-threshold=["']?(\d+)/);
        if (m) { thresholdCents = Math.max(0, parseInt(m[1], 10) || 15000); }
        refreshFromBundle();
      })
      .catch(function () { scheduleDelayedRefreshes(); });
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      var fromCheckout = document.referrer && document.referrer.indexOf('checkout') !== -1;
      if (fromCheckout) {
        fetchAndRefreshBar();
      }
      scheduleDelayedRefreshes();
    }
  });

  // Replace _600x with _800x for Recharge Bundle images open modal
  function replaceImageSize(img) {
    if (!img) return;
  
    const currentSrc = img.getAttribute('src');
  
    if (!currentSrc) return;
  
    // Replace _600x with _800x
    const updatedSrc = currentSrc.replace(
      /_600x(\.(png|jpg|jpeg|webp))/i,
      '_800x$1'
    );
  
    // Update only if changed
    if (updatedSrc !== currentSrc) {
      img.src = updatedSrc;
    }
  }
  
  function updateRechargeImages(scope = document) {
    scope.querySelectorAll('.recharge-image img').forEach((img) => {
      replaceImageSize(img);
  
      // Also watch each image for src changes
      if (!img.dataset.rechargeObserved) {
        img.dataset.rechargeObserved = 'true';
  
        const imgObserver = new MutationObserver(() => {
          replaceImageSize(img);
        });
  
        imgObserver.observe(img, {
          attributes: true,
          attributeFilter: ['src']
        });
      }
    });
  }
  
  // Initial run
  updateRechargeImages();
  
  // Watch for dynamically added Recharge content
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
  
        updateRechargeImages(node);
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Extra fallback for thumbnail click updates
  document.addEventListener('click', () => {
    setTimeout(() => {
      updateRechargeImages();
    }, 100);
  });

})();
